/**
 * `openDB` with the safety net the bare call does not have
 *
 * An IndexedDB open has outcomes beyond success and error, and `idb` reports
 * none of them unless you ask. Unhandled, they all land in the same place: a
 * promise that never settles.
 *
 *   blocking   — another tab wants a newer schema and *we* are the connection
 *                holding the old one open. Nobody closes, so its upgrade never
 *                starts. This is a deadlock rather than a race: refreshing the
 *                stuck tab cannot clear it, because we are the one holding on.
 *   blocked    — we are the tab waiting on someone else. Normally momentary,
 *                since their `blocking` closes them, so this only logs; the
 *                timeout is what actually settles it if they never do.
 *   terminated — the browser closed the connection under us.
 *
 * The timeout is the backstop for all of them, and for a storage-level fault
 * where the request fires no event at all — Firefox has more ways to produce
 * that than Chrome does. A rejected open is an error a caller can surface and
 * retry; a pending one is an eternal spinner.
 *
 * `onLost` is how a caller that caches the handle learns to stop: after it
 * fires, the previous promise is dead and the next use must reopen.
 */

import { openDB } from 'idb';

// The open has to fail, and it has to fail quickly.
//
// Every consumer of the store already degrades gracefully when a read throws:
// the dashboard falls back to the network, sync skips its hydrate, the worker
// handshake gives up. None of them survive a read that simply never returns —
// `Dashboard.fetchFiles` awaits the cache before it will even try the network,
// so a pending open freezes the library online as well as off.
//
// 5s warns, which is what tells the two cases apart in the console. 10s gives
// up, bounding the freeze while leaving headroom for storage that is slow
// rather than broken. A false positive here costs a network round trip; a
// missing one costs the whole screen.
export const SLOW_OPEN_MS = 5000;
export const OPEN_TIMEOUT_MS = 10000;

// A store that just failed is very unlikely to work on the next read, and
// dropping the cached handle means *every* read starts a fresh open. Without
// this, one broken store becomes a stream of 10s stalls instead of one.
export const FAILURE_COOLDOWN_MS = 30000;
const failedAt = new Map();

const withTimeout = (name, opening) => {
  const slow = setTimeout(
    () => console.warn(`idb: opening ${name} is still pending after ${SLOW_OPEN_MS}ms`),
    SLOW_OPEN_MS,
  );
  let timer;
  const stall = new Promise((_, reject) => {
    timer = setTimeout(() => {
      // Past this point the open is pathological, so a late arrival is an
      // orphan that would block the next upgrade. Close it if it ever lands.
      opening.then((db) => db.close()).catch(() => {});
      reject(new Error(`idb: opening ${name} timed out after ${OPEN_TIMEOUT_MS}ms`));
    }, OPEN_TIMEOUT_MS);
  });
  return Promise.race([opening, stall]).finally(() => {
    clearTimeout(slow);
    clearTimeout(timer);
  });
};

/**
 * Open a database, giving up rather than hanging.
 * @param {string} name
 * @param {number} version
 * @param {{ upgrade?: Function, onLost?: (why: string) => void }} options
 */
export const openDbSafely = (name, version, { upgrade, onLost } = {}) => {
  const since = Date.now() - (failedAt.get(name) ?? -Infinity);
  if (since < FAILURE_COOLDOWN_MS) {
    return Promise.reject(
      new Error(`idb: ${name} failed to open ${since}ms ago; not retrying yet`),
    );
  }

  let handle = null;
  const lost = (why) => {
    handle?.close();
    handle = null;
    onLost?.(why);
  };

  const opening = openDB(name, version, {
    upgrade,
    blocked: (current, wanted) =>
      console.warn(`idb: ${name} v${wanted} is blocked by an open v${current} connection`),
    // Let go so the other tab's upgrade can proceed. Our handle is stale by
    // definition — it is the old schema they are waiting to replace.
    blocking: () => lost('blocking'),
    terminated: () => lost('terminated'),
  }).then((db) => {
    handle = db;
    failedAt.delete(name);
    return db;
  });

  return withTimeout(name, opening).catch((e) => {
    // A caller caching this promise must drop it, or one bad open becomes
    // permanent. The assignment has already happened: rejection is async.
    failedAt.set(name, Date.now());
    lost('failed');
    throw e;
  });
};
